import {
  Alert,
  FileDropField,
  FileList,
  Section,
  Select,
  Stack,
  Title
} from '@efast_public/fjd-component-library';
import { ReactElement, useState } from 'react';
import { SelectOption } from '../../../../shared/utils/antragsuebersicht';
import './DataModelUpload.css';
import DataModelUploadWrapper from './DataModelUploadWrapper';
import { ImportDatamodelDto } from '../../../../api/hooks/useDataModel';
import { fileToString } from '../../../../shared/utils/fileUtils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BehApiRoutes, behClient } from '../../../../api/beh-api';
import { useNotification } from '../../../../shared/notification/useNotification';
import { useMissingDataModels } from './useMissingDataModels';
import MissingDataModelsAlert from './MissingDataModelsAlert';

interface DatamodelUploadModalProps {
  readonly isOpen: boolean;
  readonly setOpenModal: (value: boolean) => void;
  readonly isComponent?: boolean;
  readonly onClose?: () => void;
}

interface SelectedIdentifier {
  label?: string;
  version?: string;
  labelReadOnly: boolean;
  versionReadOnly: boolean;
}

function DatamodelUpload(props: DatamodelUploadModalProps): ReactElement {
  const { notifyUserFromRequest, notifyUser } = useNotification();
  const queryClient = useQueryClient();
  const {
    mutateAsync: updateDatamodels,
    error: updateError,
    isPending: isLoading
  } = useMutation({
    mutationFn: (importModel: Array<ImportDatamodelDto>) =>
      notifyUserFromRequest(
        () => behClient.put(BehApiRoutes.datamodels(), importModel),
        {
          successMessage:
            'Ein oder mehrere Datenmodell(e) wurden erfolgreich hochgeladen.',
          errorMessage:
            'Ein oder mehrere Datenmodell(e) konnten nicht hochgeladen werden.'
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [BehApiRoutes.datamodelsList()]
      });
      queryClient.invalidateQueries({
        queryKey: [BehApiRoutes.destinationsLastSync()]
      });
    }
  });

  // Use the shared hook for missing data models
  const {
    dataModels: groupedDataModels,
    missingDataModels,
    hasMissingModels,
    isLoadingDatamodels,
    getAvailableIdentifiers: getAvailableIdentifiersFromHook
  } = useMissingDataModels();

  // Get available identifiers for the identifier select
  const getAvailableIdentifiers = (): SelectOption[] => {
    return getAvailableIdentifiersFromHook();
  };

  // Get already selected versions (identifier + version combinations) by other files
  const getSelectedVersions = (excludeFile?: File): Set<string> => {
    const selectedVersions = new Set<string>();
    Array.from(uploadedFiles.entries()).forEach(([file, data]) => {
      if (file !== excludeFile && data?.label && data?.version) {
        selectedVersions.add(`${data.label}:${data.version}`);
      }
    });
    return selectedVersions;
  };

  // Get the selected version option that matches the options format
  const getSelectedVersionOption = (file: File): SelectOption[] => {
    const currentVersion = uploadedFiles.get(file)?.version;
    const currentIdentifier = uploadedFiles.get(file)?.label;

    if (!currentVersion || !currentIdentifier) {
      return [];
    }

    // Return clean version without extra text for selected option
    const selectedOption = {
      label: currentVersion,
      value: currentVersion
    };

    return [selectedOption];
  };

  const [uploadedFiles, setUploadedFiles] = useState(
    new Map<File, SelectedIdentifier>()
  );

  const addNewItem = async (key: File, value: string | null) => {
    if (
      key.type !== 'application/json' &&
      key.type !== 'application/xml' &&
      key.type !== 'text/xml'
    ) {
      notifyUser({
        intent: 'error',
        title:
          'Ungültiges Dateiformat. Es sind nur Dateien im Format XML oder JSON zulässig.'
      });
      return;
    }

    // Manual selection - update only the label, keep other properties
    setUploadedFiles((prevMap) => {
      const newMap = new Map(prevMap);
      const existingValue = newMap.get(key);
      const updatedValue: SelectedIdentifier = {
        label: value || undefined,
        version: existingValue?.version || undefined,
        labelReadOnly: existingValue?.labelReadOnly || false,
        versionReadOnly: existingValue?.versionReadOnly || false
      };
      newMap.set(key, updatedValue);
      return newMap;
    });
  };

  const updateVersion = (key: File, version: string) => {
    setUploadedFiles((prevMap) => {
      const newMap = new Map(prevMap);
      const existingValue = newMap.get(key);
      if (existingValue) {
        const updatedValue: SelectedIdentifier = {
          ...existingValue,
          version: version || undefined
        };
        newMap.set(key, updatedValue);
      }
      return newMap;
    });
  };

  // Get available versions for a specific identifier
  const getVersionsForIdentifier = (
    identifier: string | null,
    currentFile?: File
  ): SelectOption[] => {
    if (!identifier || !groupedDataModels[identifier]) {
      return [];
    }

    const modelsForIdentifier = groupedDataModels[identifier];
    const selectedVersions = getSelectedVersions(currentFile);

    const options = modelsForIdentifier.map((model, index) => {
      const version = model.datamodelVersion || `Version ${index + 1}`;

      const versionKey = `${identifier}:${version}`;
      const isAlreadySelected = selectedVersions.has(versionKey);

      const option = {
        label: !model.isDataModelProvided
          ? `⚠️ (Datenmodell erforderlich) ${version}`
          : `${version}`,
        value: version,
        disabled: isAlreadySelected
      };
      return option;
    });

    return options;
  };

  const removeItem = (key: File) => {
    setUploadedFiles((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.delete(key);
      return newMap;
    });
  };
  const getMimeType = (mimeType: string) => {
    if (mimeType === 'text/xml') {
      return 'application/xml';
    }

    return mimeType;
  };
  const uploadDatamodels: () => void = async () => {
    let importModels: Array<ImportDatamodelDto> = [];
    try {
      const entries = Array.from(uploadedFiles.entries());
      importModels = await Promise.all(
        entries.map(async ([file, verwaltungsleistung]) => {
          const fileString: string = await fileToString(file);
          return {
            identifier: verwaltungsleistung?.label,
            importModel: fileString,
            mimeType: getMimeType(file.type),
            version: verwaltungsleistung.version
          } as ImportDatamodelDto;
        })
      );
    } catch {
      notifyUser({
        intent: 'error',
        title:
          'Beim Lesen der Datei(en) ist leider ein Fehler aufgetreten. Bitte versuchen Sie es erneut.'
      });
      return;
    }
    updateDatamodels(
      importModels.filter(
        (value: ImportDatamodelDto) => value.identifier !== null
      )
    );
  };

  const onClose = () => {
    props.setOpenModal(false);
    setUploadedFiles(new Map());
    if (props.onClose) {
      props.onClose();
    }
  };

  const hasNullValuesWithoutMatchingError: boolean = Array.from(
    uploadedFiles.values()
  ).some((value: SelectedIdentifier | null) => {
    if (value === null) {
      return false;
    }

    return value.label === null;
  });

  const modalContent: ReactElement = (
    <Section>
      {props.isComponent ? (
        <>
          <Title level={2} visualLevel={4}>
            Laden Sie Datenmodelle für einzelne Verwaltungsleistungen hoch
          </Title>
          <br />
        </>
      ) : (
        <div className="contentHeading">
          Laden Sie Datenmodelle für einzelne Verwaltungsleistungen hoch
        </div>
      )}
      <div className="descriptionText">
        Sobald Sie eine oder mehrere Dateien ausgewählt haben, können Sie diese
        der jeweils zugehörigen Verwaltungsleistung zuordnen. Erlaubte Formate:
        JSON, XML.
      </div>
    </Section>
  );

  return (
    <DataModelUploadWrapper
      onClose={onClose}
      onSave={uploadDatamodels}
      isLoading={isLoading}
      isOpen={props.isOpen}
      isComponent={props.isComponent ?? false}
      isPrimaryButtonDisabled={
        hasNullValuesWithoutMatchingError || uploadedFiles.size === 0
      }
    >
      <Stack direction="column" spacing="xxl" alignItems="stretch">
        {modalContent}
        <MissingDataModelsAlert
          missingDataModels={missingDataModels}
          hasMissingModels={hasMissingModels}
        />
        <FileDropField
          disabled={isLoading}
          label="Datenmodelle hochladen"
          data-testid={'dataModel_file_drop_field'}
          dropHelperText="oder in dieses Feld ziehen"
          onAdd={(files) => files.map((file) => addNewItem(file, null))}
        />
        <FileList>
          {uploadedFiles &&
            Array.from(uploadedFiles.keys()).map((file, index) => (
              <Stack
                direction="row"
                spacing="l"
                key={`fileStack_${index}`}
                style={{ width: '100%', justifyContent: 'space-between' }}
              >
                <FileList.FileItem
                  className="datamodel-file-list-item"
                  file={file}
                  onDelete={() => removeItem(file)}
                />
                <Stack
                  direction="column"
                  spacing="m"
                  justifyContent="center"
                  style={{ minWidth: '40rem' }}
                >
                  <Stack direction="column" spacing="xs">
                    <div>Zugehörige Verwaltungsleistung</div>
                    <Select
                      selectedOptions={[
                        {
                          label: uploadedFiles.get(file)?.label ?? '',
                          value: uploadedFiles.get(file)?.label ?? ''
                        }
                      ]}
                      data-testid={`dataModel-select-${file.name}`}
                      disabled={
                        uploadedFiles.get(file)?.labelReadOnly || isLoading
                      }
                      options={getAvailableIdentifiers()}
                      translations={{
                        deleteText: 'Löschen'
                      }}
                      selectType="single"
                      onChange={(selectedItems) =>
                        selectedItems.length > 0
                          ? addNewItem(file, selectedItems[0].value)
                          : addNewItem(file, null)
                      }
                    />
                  </Stack>
                  <Stack direction="column" spacing="xs">
                    <div>Datenmodell Version</div>
                    <Select
                      selectedOptions={getSelectedVersionOption(file)}
                      data-testid={`dataModel-version-${file.name}`}
                      disabled={
                        uploadedFiles.get(file)?.versionReadOnly ||
                        isLoading ||
                        isLoadingDatamodels ||
                        !uploadedFiles.get(file)?.label
                      }
                      options={getVersionsForIdentifier(
                        uploadedFiles.get(file)?.label ?? null,
                        file
                      )}
                      translations={{
                        deleteText: 'Löschen'
                      }}
                      selectType="single"
                      placeholder="Version auswählen"
                      onChange={(selectedItems) =>
                        selectedItems.length > 0
                          ? updateVersion(file, selectedItems[0].value)
                          : updateVersion(file, '')
                      }
                    />
                  </Stack>
                </Stack>
              </Stack>
            ))}
        </FileList>
      </Stack>

      <Stack
        direction="column"
        spacing="xs"
        alignItems="stretch"
        style={{
          width: '100%',
          paddingTop: '1.75rem',
          paddingBottom: '0.2rem'
        }}
      >
        {uploadedFiles.size > 0 && !updateError && (
          <Alert
            data-testid="datamodel-alert-success"
            message="Erfolgreich hochgeladen!"
            variant="success"
          />
        )}
        {uploadedFiles.size > 0 && hasNullValuesWithoutMatchingError && (
          <Alert
            data-testid="datamodel-alert-warning"
            message="Sie haben noch nicht zu jedem Datenmodell eine zugehörige Verwaltungsleistung ausgewählt."
            variant="warning"
          />
        )}
        {updateError && (
          <Alert
            data-testid="datamodel-alert-error"
            message="Es ist ein Fehler aufgetreten. Bitten wenden Sie sich an Ihre:n Administrator:in."
            variant="error"
          />
        )}
      </Stack>
    </DataModelUploadWrapper>
  );
}

export default DatamodelUpload;
